import * as CommonAnalytics from './CommonAnalyticsBeacons'
import * as BrowseAnalytics from './BrowseAnalyticsBeacons'
import { inject } from '../di'
import { ConfigService } from '../ConfigService'
import {
    initSessionStorageSequenceId,
    getSessionStorageSequenceId,
    increaseSequenceId,
} from '../../utils/SessionStorageSequenceId'

import { AnalyticsService } from '../AnalyticsService'
import { AvroService, Options } from './AvroService'
import { getSessionId } from '../../utils/uuid'
import { AdAdaptedItem } from './generated/AnalyticsBeacons'
import { DetailedListItem } from '@adadapted/js-sdk/src'

interface BrowseAvroAnalyticsChannelInterface {
    FireBrowseAdAdaptedOpenAddToCartAd(options: Options): void
    FireBrowseAdAdaptedOpenAddToShoppingListAd(options: Options): void
    FireBrowseAdAdaptedImpressionAd(options: Options): void
    FireBrowseAdAdaptedViewableImpressionAd(options: Options): void
}

export default class BrowseAvroAnalyticsChannel
    implements BrowseAvroAnalyticsChannelInterface
{
    @inject config: ConfigService
    @inject avro: AvroService
    @inject analytics: AnalyticsService

    constructor() {
        initSessionStorageSequenceId()
    }

    public FireBrowseAdAdaptedOpenAddToCartAd(options: Options) {
        this.avro.FireAvroBeacon(this.BrowseAdAdaptedOpenAddToCartAd(options))
    }

    public FireBrowseAdAdaptedOpenAddToShoppingListAd(options: Options) {
        this.avro.FireAvroBeacon(
            this.BrowseAdAdaptedOpenAddToShoppingListAd(options)
        )
    }

    public FireBrowseAdAdaptedImpressionAd(options: Options) {
        this.avro.FireAvroBeacon(this.BrowseAdAdaptedImpressionAd(options))
    }

    public FireBrowseAdAdaptedViewableImpressionAd(options: Options) {
        this.avro.FireAvroBeacon(
            this.BrowseAdAdaptedViewableImpressionAd(options)
        )
    }

    private BrowseAdAdaptedOpenAddToCartAd(
        options: Options
    ): BrowseAnalytics.BrowseAdAdaptedOpenAddToCartAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedOpenAddToCartAdSchemaId
        )
    }

    private BrowseAdAdaptedOpenAddToShoppingListAd(
        options: Options
    ): BrowseAnalytics.BrowseAdAdaptedOpenAddToShoppingListAd {
        const listAdAdaptedItems: AdAdaptedItem[] = options.items.map(
            (item: DetailedListItem) => ({
                productBarcode: item.product_barcode,
                productBrand: item.product_brand,
                productCategory: item.product_category,
                productDiscount: item.product_discount,
                productImage: item.product_image,
                productSku: item.product_sku,
                productTitle: item.product_title,
                trackingId: item.tracking_id,
            })
        )
        return {
            listAdAdaptedItems,
            ...this.getCommonFields(
                options,
                BrowseAnalytics.Schemas
                    .BrowseAdAdaptedOpenAddToShoppingListAdSchemaId
            ),
        }
    }

    private BrowseAdAdaptedImpressionAd(
        options: Options
    ): BrowseAnalytics.BrowseAdAdaptedImpressionAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedImpressionAdSchemaId
        )
    }

    private BrowseAdAdaptedViewableImpressionAd(
        options: Options
    ): BrowseAnalytics.BrowseAdAdaptedViewableImpressionAd {
        return this.getCommonFields(
            options,
            BrowseAnalytics.Schemas.BrowseAdAdaptedViewableImpressionAdSchemaId
        )
    }

    private getCommonFields(options: Options, schemaId: any) {
        return {
            base: this.avro.getBase(CommonAnalytics.ApplicationID.FlippWeb),
            flippWebBase: this.getFlippWebBase(),
            flippWebBaseExtended: this.getFlippWebBaseExtended(),
            userAccount: {
                'com.flipp.beacon.common.entity.UserAccount':
                    this.getUserAccount(),
            },
            browseContext: this.getBrowseContext(),
            adAdaptedAdContext: this.getAdAdaptedAdContext(options),
            schemaInfo: {
                schemaId,
                schemaName: null,
            } as any,
        }
    }

    private getFlippWebBase(): BrowseAnalytics.FlippWebBase {
        increaseSequenceId()

        return {
            appVersion: this.config.BUILD,
        }
    }

    private getFlippWebBaseExtended(): BrowseAnalytics.FlippWebBaseExtended {
        increaseSequenceId()

        return {
            environment: this.avro.getEnvironment(),
            clientParameters: this.avro.getClientParameters(),
            locationSource: this.avro.getLocationSource(),
            epochMilliseconds: new Date().getTime(),
            sessionId: this.analytics.sessionId,
            sequenceId: getSessionStorageSequenceId(),
        }
    }

    private getUserAccount(): CommonAnalytics.UserAccount {
        let accountId = this.analytics.getAccountGuid()
        if (!accountId) accountId = getSessionId()
        return { accountId }
    }

    private getBrowseContext(): BrowseAnalytics.BrowseContext {
        return { pageName: document.title }
    }

    private getAdAdaptedAdContext(
        options: Options
    ): BrowseAnalytics.AdAdaptedAdContext {
        return {
            id: options.id,
            zoneId: options.zoneId,
        }
    }
}
